import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { adminApi, mockData, User } from '../api/client';

const columnHelper = createColumnHelper<User>();

export default function Users() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [page, setPage] = useState(0);
  const [tierFilter, setTierFilter] = useState<'free' | 'pro' | 'team' | 'enterprise' | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'suspended' | ''>('');
  const pageSize = 10;

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', page, tierFilter, statusFilter],
    queryFn: () => adminApi.getUsers({
      limit: pageSize,
      offset: page * pageSize,
      tier: tierFilter || undefined,
      status: statusFilter || undefined,
    }),
    placeholderData: { users: mockData.users(), total: mockData.users().length },
  });

  const users = usersData?.users || [];

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-slate-300">
                {info.getValue().charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="font-medium text-white">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('tier', {
        header: 'Tier',
        cell: (info) => {
          const tier = info.getValue();
          const colors: Record<string, string> = {
            enterprise: 'bg-purple-500/20 text-purple-400',
            pro: 'bg-blue-500/20 text-blue-400',
            free: 'bg-slate-600 text-slate-300',
          };
          return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colors[tier] || colors.free}`}>
              {tier}
            </span>
          );
        },
      }),
      columnHelper.accessor('credits', {
        header: 'Credits',
        cell: (info) => {
          const credits = info.getValue();
          const used = info.row.original.creditsUsed;
          const percentage = Math.round((used / credits) * 100);
          return (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-white">{used.toLocaleString()}</span>
                <span className="text-slate-400">/ {credits.toLocaleString()}</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          const colors: Record<string, string> = {
            active: 'bg-green-500/20 text-green-400',
            inactive: 'bg-yellow-500/20 text-yellow-400',
            suspended: 'bg-red-500/20 text-red-400',
          };
          return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colors[status]}`}>
              {status}
            </span>
          );
        },
      }),
      columnHelper.accessor('churnRisk', {
        header: 'Churn Risk',
        cell: (info) => {
          const risk = info.getValue();
          const colors: Record<string, string> = {
            low: 'bg-green-500/20 text-green-400',
            medium: 'bg-yellow-500/20 text-yellow-400',
            high: 'bg-red-500/20 text-red-400',
          };
          return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colors[risk]}`}>
              {risk}
            </span>
          );
        },
      }),
      columnHelper.accessor('lastActiveAt', {
        header: 'Last Active',
        cell: (info) => {
          const date = new Date(info.getValue());
          return (
            <span className="text-sm text-slate-400">
              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <button
            onClick={() => setSelectedUser(info.row.original)}
            className="text-primary-400 hover:text-primary-300 font-medium text-sm"
          >
            View Details
          </button>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Users</h1>
        <p className="text-slate-400 mt-1">Manage your portal users</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Search users..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="input"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value as typeof tierFilter);
              setPage(0);
            }}
            className="input w-auto"
          >
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="team">Team</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(0);
            }}
            className="input w-auto"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50 border-b border-slate-700">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-700"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' ^',
                          desc: ' v',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-700">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-700/50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
          <div className="text-sm text-slate-400">
            {isLoading ? (
              'Loading...'
            ) : (
              <>
                Showing {page * pageSize + 1} to{' '}
                {Math.min((page + 1) * pageSize, usersData?.total || 0)}{' '}
                of {usersData?.total || 0} users
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isLoading}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={isLoading || (page + 1) * pageSize >= (usersData?.total || 0)}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">User Details</h2>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-medium text-slate-300">
                    {selectedUser.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">{selectedUser.email}</h3>
                  <p className="text-slate-400">User ID: {selectedUser.id}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Tier</p>
                  <p className="text-lg font-medium text-white capitalize">{selectedUser.tier}</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Status</p>
                  <p className="text-lg font-medium text-white capitalize">{selectedUser.status}</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Credits Used</p>
                  <p className="text-lg font-medium text-white">
                    {selectedUser.creditsUsed.toLocaleString()} / {selectedUser.credits.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Churn Risk</p>
                  <p className="text-lg font-medium text-white capitalize">{selectedUser.churnRisk}</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Created At</p>
                  <p className="text-lg font-medium text-white">
                    {new Date(selectedUser.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-sm text-slate-400">Last Active</p>
                  <p className="text-lg font-medium text-white">
                    {new Date(selectedUser.lastActiveAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-700">
                <button className="btn-primary flex-1">Edit User</button>
                <button className="btn-secondary flex-1">Reset Password</button>
                <button className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors font-medium flex-1">
                  Suspend Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
